import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ACTIVITY_LOG,
  REJECTION_REASON,
  TRAVEL_REQUESTS,
  buildTimeline,
  getRequestInfo,
} from "./travelData";
import "./travel.css";

/* Travel Request detail — ported from the prototype (view-travel.html).
   Status-aware: banner, badge, Amend visibility, Approved Costs card and the
   Approval Progress timeline all adapt to the request status, and the
   insurance rows adapt to annual vs per-trip coverage. */

const STATUS_BADGE = {
  Approved: "request-status-approved",
  Pending: "request-status-pending",
  Rejected: "request-status-rejected",
  Completed: "request-status-completed",
};

const LOG_BADGE = {
  info: "log-badge log-badge-info",
  amend: "log-badge log-badge-amend",
  reject: "log-badge log-badge-reject",
  muted: "log-badge log-badge-muted",
  approved: "status-badge status-active",
};

const TIMELINE_ICON = { done: "ri-check-line", rejected: "ri-close-line" };

const TravelDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const requestId = (id || "TR-2023-041").trim();
  const info = getRequestInfo(requestId);
  const { status, insurance } = info;

  const listRow = useMemo(() => TRAVEL_REQUESTS.find((row) => row.id === requestId), [requestId]);
  const [activeTab, setActiveTab] = useState("details");

  const timeline = useMemo(() => buildTimeline(status), [status]);

  const showCostsCard = status !== "Pending" && status !== "Rejected";
  const completedPerTrip = insurance === "per-trip" && status === "Completed";

  const insuranceDoc =
    insurance === "annual"
      ? { badge: "Covered — Annual Policy", badgeClass: "status-badge status-active", note: "Staff is covered by the company annual travel insurance policy." }
      : completedPerTrip
      ? { badge: "Uploaded", badgeClass: "status-badge status-active", note: "No annual policy — Admin purchases insurance for this trip." }
      : { badge: "Awaiting Admin", badgeClass: "status-badge status-inactive", note: "No annual policy — Admin purchases insurance for this trip." };

  const insuranceCost =
    insurance === "annual"
      ? { detail: "Covered under the company annual policy — no per-trip purchase needed", amount: "Included", muted: false }
      : completedPerTrip
      ? { detail: "5 days international coverage · purchased by Admin", amount: "65.00", muted: false }
      : { detail: "5 days international coverage · pending Admin confirmation", amount: "—", muted: true };

  const approvedTotal = completedPerTrip ? "RM 3,235.00" : "RM 3,170.00";

  return (
    <div className="bes-travel bes-travel-detail">
      <div className="detail-breadcrumb text-muted">
        <Link to="/travel">Travel Requests</Link> <span className="sep">/</span>{" "}
        <span className="current">{requestId}</span>
      </div>

      <div className="request-detail-header">
        <div>
          <div className="request-detail-title">
            <h1>{requestId}</h1>
            <span className={`status-badge ${STATUS_BADGE[status] || "request-status-approved"}`}>{status}</span>
          </div>
          <p className="request-detail-meta">
            Submitted by Eldren Aniell Dunggat · 12 Jun 2026, 10:22 am · Last updated{" "}
            {listRow ? listRow.updated : "12 Jun 2026, 10:22 am"}
          </p>
        </div>
        <div className="detail-header-actions">
          <Link to="/travel" className="btn btn-outline">
            <i className="ri-arrow-left-line" /> Back to list
          </Link>
          {status === "Approved" && (
            <button type="button" className="btn btn-primary" onClick={() => navigate(`/travel/${requestId}/amend`)}>
              <i className="ri-edit-line" /> Amend
            </button>
          )}
        </div>
      </div>

      {status === "Rejected" && (
        <div className="warning-alert alert-danger rejection-banner">
          <i className="ri-error-warning-fill" />
          <p>
            <strong>Request rejected.</strong> {REJECTION_REASON}
          </p>
        </div>
      )}

      <div className="custom-tabs">
        <button
          type="button"
          className={`tab-btn ${activeTab === "details" ? "active" : ""}`}
          onClick={() => setActiveTab("details")}
        >
          Details
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === "logs" ? "active" : ""}`}
          onClick={() => setActiveTab("logs")}
        >
          Logs
        </button>
      </div>

      {activeTab === "details" ? (
        <div className="travel-layout-grid">
          <div className="travel-left-col">
            {/* 1. Traveler & Trip Information */}
            <div className="form-card">
              <div className="card-header-dark">
                <div className="section-title-numbered">
                  <span>1</span>
                  <h2>Traveler &amp; Trip Information</h2>
                </div>
              </div>
              <div className="form-card-body">
                <div className="review-grid">
                  <div className="review-item">
                    <span className="review-label">NAME</span>
                    <span className="review-value">Eldren Aniell Dunggat</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">TITLE</span>
                    <span className="review-value">Manager</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">DEPARTURE</span>
                    <span className="review-value">25 August 2026</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">RETURN</span>
                    <span className="review-value">30 August 2026</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">DURATION</span>
                    <span className="review-value text-red strong">6 Days / 5 Nights</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">DESTINATION</span>
                    <span className="review-value">Marina Bay Sands, Singapore</span>
                  </div>
                </div>
                <div className="review-item review-item-wide">
                  <span className="review-label">PURPOSE</span>
                  <span className="review-value">Attend ITB Asia 2026 business events and investment meetings.</span>
                </div>
              </div>
            </div>

            {/* 2. Travel Schedule & Accommodation */}
            <div className="form-card">
              <div className="card-header-dark">
                <div className="section-title-numbered">
                  <span>2</span>
                  <h2>Travel Schedule &amp; Accommodation</h2>
                </div>
              </div>
              <div className="form-card-body no-pad">
                <div className="schedule-block">
                  <table className="review-schedule-table">
                    <thead>
                      <tr>
                        <th className="w-20">DATE</th>
                        <th>ACTIVITY</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>25/08/2026</td>
                        <td>Depart Kuching, Sarawak → Arrive Singapore</td>
                      </tr>
                      <tr>
                        <td>26–28/08/2026</td>
                        <td>Attend ITB Asia 2026 Day 01–03</td>
                      </tr>
                      <tr>
                        <td>29/08/2026</td>
                        <td>Investment meetings · depart for return leg</td>
                      </tr>
                      <tr>
                        <td>30/08/2026</td>
                        <td>Arrive Kuching, Sarawak (final stop)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="destinations-block">
                  <div className="table-responsive">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Destination</th>
                          <th>Arrival</th>
                          <th>Departure</th>
                          <th>Transport</th>
                          <th>Hotel</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="font-medium">Singapore</td>
                          <td>25/08/2026</td>
                          <td>29/08/2026</td>
                          <td>Air</td>
                          <td>
                            <div className="hotel-line">Marina Bay Sands · 3 nights · 1 person · No breakfast</div>
                            <div className="hotel-line">Hotel Boss · 1 night · 1 person · With breakfast</div>
                          </td>
                        </tr>
                        <tr>
                          <td className="font-medium">
                            Kuching, Sarawak <span className="status-badge status-active final-badge">Final</span>
                          </td>
                          <td>30/08/2026</td>
                          <td>—</td>
                          <td>Air</td>
                          <td className="text-muted">Return home — no hotel</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Cash Advance */}
            <div className="form-card">
              <div className="card-header-dark">
                <div className="section-title-numbered">
                  <span>3</span>
                  <h2>Cash Advance</h2>
                </div>
              </div>
              <div className="form-card-body">
                <div className="review-grid">
                  <div className="review-item">
                    <span className="review-label">TRAVEL TYPE</span>
                    <span className="review-value">International</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">PREVIOUS CLAIM SUBMITTED</span>
                    <span className="review-value">Yes</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">UNUSUAL CIRCUMSTANCES</span>
                    <span className="review-value">No</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">REQUESTING CASH ADVANCE</span>
                    <span className="review-value">Yes</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">REQUESTED AMOUNT</span>
                    <span className="review-value">RM 870.00</span>
                  </div>
                </div>
                <div className="review-item review-item-wide">
                  <span className="review-label">REASON</span>
                  <span className="review-value">Transportation and meals during ITB Asia 2026 in Singapore.</span>
                </div>
              </div>
            </div>

            {/* 4. Documents */}
            <div className="form-card">
              <div className="card-header-dark">
                <div className="section-title-numbered">
                  <span>4</span>
                  <h2>Documents</h2>
                </div>
              </div>
              <div className="form-card-body">
                <div className="doc-section-title">
                  <span>Required Documents</span>
                  <small>Uploaded by requestor</small>
                </div>
                {["Event Invitation / Agenda", "Travel Itinerary", "Hotel Quotation", "Passport / Visa Copy"].map(
                  (doc) => (
                    <div className="doc-status-row" key={doc}>
                      <span className="review-value">{doc}</span>
                      <span className="status-badge status-active">Uploaded</span>
                    </div>
                  )
                )}

                <div className="doc-section-title booking-docs">
                  <span>Booking Documents</span>
                  <small>Uploaded by Admin after approval</small>
                </div>
                <div className="doc-status-row">
                  <span className="review-value">Flight Booking Confirmation</span>
                  <span className="status-badge status-active">Uploaded</span>
                </div>
                <div className="doc-status-row">
                  <span className="review-value">Hotel Booking Confirmation</span>
                  <span className="status-badge status-active">Uploaded</span>
                </div>
                <div className="doc-status-row">
                  <span className="review-value">
                    Travel Insurance
                    <small className="text-muted insurance-note">{insuranceDoc.note}</small>
                  </span>
                  <span className={insuranceDoc.badgeClass}>{insuranceDoc.badge}</span>
                </div>
              </div>
            </div>

            {/* 5. Approved Costs (filled by Admin) */}
            {showCostsCard && (
              <div className="form-card last-card">
                <div className="card-header-dark">
                  <div className="section-title-numbered">
                    <span>5</span>
                    <h2>Approved Costs</h2>
                  </div>
                  <span className="header-note-light">Filled in by Admin during approval</span>
                </div>
                <div className="form-card-body">
                  <div className="table-responsive">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th className="w-26">Item</th>
                          <th>Details</th>
                          <th className="w-18 text-right">Cost (RM)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="font-medium">Flight Ticket</td>
                          <td className="text-muted">Kuching ⇄ Singapore · return · economy</td>
                          <td className="text-right cost-value">1,850.00</td>
                        </tr>
                        <tr>
                          <td className="font-medium">Hotel</td>
                          <td className="text-muted">Marina Bay Sands · 3 nights · 1 person</td>
                          <td className="text-right cost-value">1,140.00</td>
                        </tr>
                        <tr>
                          <td className="font-medium">Hotel</td>
                          <td className="text-muted">Hotel Boss · 1 night · 1 person · with breakfast</td>
                          <td className="text-right cost-value">180.00</td>
                        </tr>
                        <tr>
                          <td className="font-medium">Travel Insurance</td>
                          <td className="text-muted">{insuranceCost.detail}</td>
                          <td className={`text-right ${insuranceCost.muted ? "text-muted" : "cost-value"}`}>
                            {insuranceCost.amount}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="approved-total-row">
                    <span>Total Approved Cost</span>
                    <strong>{approvedTotal}</strong>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right column widgets */}
          <div className="travel-right-col">
            <div className="widget-card summary-widget">
              <div className="widget-header-dark">
                <h3>Trip Summary</h3>
              </div>
              <div className="widget-body">
              <div className="summary-group">
                <h5>REQUEST TYPE</h5>
                <p className="strong">International Travel</p>
              </div>
              <div className="summary-group">
                <h5>DESTINATION</h5>
                <p className="strong">Marina Bay Sands, Singapore</p>
              </div>
              <div className="summary-group">
                <h5>DURATION</h5>
                <p className="text-red strong">6 Days / 5 Nights</p>
              </div>
              <div className="summary-group">
                <h5>BUDGET CODE(S)</h5>
                <p>BE-OPS-001</p>
              </div>
              <div className="summary-group">
                <h5>CASH ADVANCE</h5>
                <p>RM 870.00</p>
              </div>
              {showCostsCard && (
                <div className="summary-group">
                  <h5>APPROVED COST (BY ADMIN)</h5>
                  <p className="strong">{approvedTotal}</p>
                </div>
              )}
              </div>
            </div>

            <div className="widget-card timeline-widget">
              <div className="widget-header-dark">
                <h3>Approval Progress</h3>
              </div>
              <div className="widget-body">
              <div className="approval-timeline">
                {timeline.map((step) => (
                  <div className={`timeline-step is-${step.state}`} key={step.title}>
                    <span className="timeline-dot">
                      {TIMELINE_ICON[step.state] ? <i className={TIMELINE_ICON[step.state]} /> : null}
                    </span>
                    <div className="timeline-title">{step.title}</div>
                    <div className="timeline-meta">{step.meta}</div>
                  </div>
                ))}
              </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="form-card last-card">
          <div className="card-header-dark">
            <div className="section-title-numbered">
              <h2>Activity Log</h2>
            </div>
            <span className="header-note-light">All changes, approvals, rejections, and amendments — newest first</span>
          </div>
          <div className="form-card-body no-pad">
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="w-17">Date / Time</th>
                    <th className="w-21">By</th>
                    <th className="w-14">Action</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {ACTIVITY_LOG.map((entry) => (
                    <tr key={`${entry.time}-${entry.action}`}>
                      <td className="text-muted">{entry.time}</td>
                      <td className="font-medium">
                        {entry.by} <small className="text-muted">({entry.role})</small>
                      </td>
                      <td>
                        <span className={LOG_BADGE[entry.badge] || LOG_BADGE.muted}>{entry.action}</span>
                      </td>
                      <td>{entry.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelDetailPage;
